import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { RolloutTarget } from '@/lib/software-update-rollout.service';
import { rolloutTargetFieldDefs } from '@/lib/software-update-rollout-rules';
import { typography as t } from '@/theme/tokens';

interface RolloutTargetInlineProps {
  target: RolloutTarget;
  /**
   * Plain-text label (the server-built `target_display_name`) used only for the
   * group's tooltip and aria-label. Visible content is built from `target`.
   */
  displayName?: string;
  /**
   * Extra styles merged onto the inline-flex root. Callers use this to constrain
   * width / wrapping in dense single-line rows. Avoid `overflow` here — it makes
   * the browser synthesize the box baseline from its bottom edge, which breaks
   * baseline alignment with sibling text; clip on an ancestor instead.
   */
  sx?: SxProps<Theme>;
}

/**
 * Keyed inline text — no chips, no container, no bold. One "key + value" pair per
 * SET target field: a small dim uppercase key followed by a regular-weight value.
 * Pairs are gap-separated (no operator glyph), each pair stays on one line, the
 * group wraps. Used in dense contexts (rollouts table, deploy summary line) where
 * it must never read as a status chip.
 */
export function RolloutTargetInline({ target, displayName, sx }: RolloutTargetInlineProps) {
  const pairs = rolloutTargetFieldDefs
    .map((f) => {
      const raw = f.get(target);
      return raw ? { key: f.shortKey, value: f.format(raw) } : null;
    })
    .filter((p): p is { key: string; value: string } => p !== null);

  if (pairs.length === 0) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        —
      </Box>
    );
  }

  const group = (
    <Box
      component="span"
      aria-label={displayName || undefined}
      sx={[
        {
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          columnGap: '14px',
          rowGap: '2px',
          verticalAlign: 'middle',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {pairs.map((p) => (
        <Box
          key={p.key}
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.625, whiteSpace: 'nowrap' }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 10.5,
              fontWeight: t.fontWeight.semibold,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.disabled',
            }}
          >
            {p.key}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: 13, fontWeight: t.fontWeight.normal, color: 'text.primary' }}
          >
            {p.value}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return displayName ? <Tooltip title={displayName}>{group}</Tooltip> : group;
}

export default RolloutTargetInline;
